// @flow

import React, { useState, useRef } from 'react';
import Grid from 'styled-components-grid';

import { withTranslation } from '@i18n';
import { useBreakpoint } from '@lib/hooks/useBreakpoint';
import { StyledCoverMaxWidth } from '@components/Wrappers/index';

import { SvgYes, SvgNo } from '@images/svg';
import { useRouter } from 'next/router';
import { ONLINE_CONSULTATION } from '@routes';
import type { PropsMiniFlow } from './types';

import TreatmentFlow from './TreatmentFlow';
import MainFlow from './MainFlow';
import { BUTTONS } from './illness';

import {
  StyledProgressBar,
  StyledHeader,
  StyledFormHeadline,
  StyledMiniFlowWrapper
} from './styles';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

const CarouselProps = {
  showThumbs: false,
  showArrows: false,
  showStatus: false,
  showIndicators: true,
  infiniteLoop: false,
  swipeable: false,
  transitionTime: 0
};

const MiniFlow = ({
  t, isHome, isMobile, treatmentPage, formName, curefyDomain, doctor, hospital, treatmentFlow
}: PropsMiniFlow) => {
  const [slide, setSlide] = useState(0);
  const [position] = useState('');
  const [leasingPosition] = useState('');
  const [time, setTime] = useState('');
  const [treatmentPageValue, setTreatmentPageValue] = useState('');
  const [success, setSuccess] = useState(false);

  const lastSlide: number = 3;
  const isLastSlide: boolean = slide === lastSlide;

  // $FlowIgnoreQueryImport
  const Mobile: boolean = useBreakpoint('mobile', 'desktop');
  const ScrollValue = Mobile ? 58 : 80;
  const scrollToRef = ref => ref.current && window.scrollTo(0, ref.current.offsetTop - ScrollValue);
  const myRef = useRef(null);
  const {
    query: { nextRoute: currentRoute }
  }: { query: { nextRoute: string } } = useRouter();

  const executeScroll = () => {
    // eslint-disable-next-line no-unused-expressions
    ((formName && Mobile) || !formName) && scrollToRef(myRef);
  };

  const handleTimeSelect = (timeType): void => {
    setTime(timeType);
    setSlide(slide + 1);
    executeScroll();
  };

  const handleTreatmentPage = (decision): void => {
    if (decision === 'yes') {
      setTreatmentPageValue(treatmentPage);
      setSlide(slide + 2);
    } else {
      setTreatmentPageValue('');
      setSlide(slide + 1);
    }
    executeScroll();
  };

  const ContentButton = () => [
    {
      label: t('MiniFlow.Yes'),
      icon: SvgYes,
      value: 'yes'
    },
    {
      label: t('MiniFlow.No'),
      icon: SvgNo,
      value: 'no'
    }
  ];

  const customHeader = (slide): string => {
    if (success) return t('MiniFlow.Success');

    if (slide === 0 && currentRoute === ONLINE_CONSULTATION) return t('MiniFlow.TreatmentCost');
    if (slide === 0 && !treatmentPage) return t('MiniFlow.BookYourAppointment');
    if (slide === 0 && treatmentPage) return t('MiniFlow.FillOut');
    if (treatmentPage && slide === 1) return t('MiniFlow.AlmostDone');
    if ((treatmentPage && slide === 2) || slide === 1) return t('MiniFlow.AlmostDone');
    if ((treatmentPage && slide === 3) || slide === 2) return t('MiniFlow.LastStep');
    return t('MiniFlow.BookYourAppointment');
  };

  const progressStage = (slide): number => {
    if (success) return 100;

    if (slide === 0) return 10;
    if (slide === 1) return 30;
    if (treatmentPage && slide === 2) return 50;
    if (slide === 3 || slide === 2) return 70;
    return 10;
  };

  const propsDetails = {
    position,
    leasingPosition,
    time,
    treatmentPageValue
  };

  return (
    <StyledMiniFlowWrapper
      ref={myRef}
      isHome={isHome}
      treatmentPage={treatmentPage}
      domain={curefyDomain}
    >
      <StyledHeader formName={formName} domain={curefyDomain}>
        <StyledFormHeadline onClick={executeScroll} formName={formName}>
          {customHeader(slide)}
        </StyledFormHeadline>
      </StyledHeader>
      <StyledProgressBar
        bgColor="linear-gradient(238deg, #ff4e00c2, #ec9f05)"
        borderRadius="none"
        height="8px"
        animateOnRender
        isLabelVisible={false}
        completed={progressStage(slide)}
        formName={formName}
      />

      <Grid>
        <Grid.Unit size={{ desktop: isLastSlide ? 1 : 1 }}>
          {treatmentPage ? (
            <TreatmentFlow
              handleTreatmentPage={handleTreatmentPage}
              handleTimeSelect={handleTimeSelect}
              treatmentPage={treatmentPage}
              slide={slide}
              doctor={doctor}
              hospital={hospital}
              ContentButton={ContentButton()}
              IllnessButtons={BUTTONS({ t })}
              isMobile={isMobile}
              propsDetails={propsDetails}
              executeScroll={executeScroll}
              CarouselProps={CarouselProps}
              setSuccess={setSuccess}
              success={success}
              formName={formName}
            />
          ) : (
            <StyledCoverMaxWidth>
              <MainFlow
                handleTimeSelect={handleTimeSelect}
                slide={slide}
                ContentButton={ContentButton()}
                IllnessButtons={BUTTONS({ t })}
                isMobile={isMobile}
                doctor={doctor}
                hospital={hospital}
                propsDetails={propsDetails}
                executeScroll={executeScroll}
                CarouselProps={CarouselProps}
                setSuccess={setSuccess}
                success={success}
                contactUs={treatmentFlow}
              />
            </StyledCoverMaxWidth>
          )}
        </Grid.Unit>
      </Grid>
    </StyledMiniFlowWrapper>
  );
};

MiniFlow.getInitialProps = (): {} => ({
  namespacesRequired: ['HowItWorksScreen']
});

export default withTranslation(['HowItWorksScreen'])(MiniFlow);
